import { useMemo } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { AccountMenuItem, SelectorSearchInput } from "./menu-parts";
import type {
  AccountMenuChoice,
  SelectorAccount,
  SelectorGroup,
  SelectorSize,
} from "./types";

export interface AccountMenuProps {
  accounts: SelectorAccount[];
  groups?: SelectorGroup[];
  value?: AccountMenuChoice;
  onChange: (choice: AccountMenuChoice) => void;
  searchable?: boolean;
  includeAll?: boolean;
  selectableGroups?: boolean;
  allLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  size?: SelectorSize;
  className?: string;
}

function normalizeGroups(
  accounts: SelectorAccount[],
  groups: SelectorGroup[]
): { groups: SelectorGroup[]; ungrouped: SelectorAccount[] } {
  const normalized =
    groups.length > 0
      ? groups.map((group) => ({
          ...group,
          accounts:
            group.accounts ||
            accounts.filter(
              (account) =>
                account.groupId === group.id ||
                account.groupLabel === group.label
            ),
        }))
      : Array.from(
          new Set(accounts.map((account) => account.groupLabel).filter(Boolean))
        ).map((label) => ({
          id: label as string,
          label: label as string,
          accounts: accounts.filter((account) => account.groupLabel === label),
        }));

  const groupedIds = new Set(
    normalized.flatMap((group) =>
      (group.accounts || []).map((account) => account.id)
    )
  );

  return {
    groups: normalized,
    ungrouped: accounts.filter((account) => !groupedIds.has(account.id)),
  };
}

export function AccountMenu({
  accounts,
  groups = [],
  value,
  onChange,
  searchable = true,
  includeAll = false,
  selectableGroups = false,
  allLabel = "All accounts",
  searchPlaceholder = "Search accounts...",
  emptyMessage = "No accounts found.",
  size = "md",
  className,
}: AccountMenuProps) {
  const normalized = useMemo(
    () => normalizeGroups(accounts, groups),
    [accounts, groups]
  );
  const selectedUngroupedIndex = normalized.ungrouped.findIndex(
    (account) => value?.kind === "account" && account.id === value.id
  );

  return (
    <Command
      className={cn(
        "h-auto rounded-lg border-neutral-100 py-1 shadow-[0_4px_8px_-2px_rgba(16,24,40,0.10),0_2px_4px_-2px_rgba(16,24,40,0.06)]",
        className
      )}
    >
      {searchable ? (
        <SelectorSearchInput
          placeholder={searchPlaceholder}
          wrapperClassName={cn(
            size === "md" ? "h-10 px-3.5" : "h-8 px-3",
            "gap-2"
          )}
          className="h-full py-0 font-inter text-sm"
        />
      ) : null}
      <CommandList
        focusable={!searchable}
        className="max-h-[min(720px,var(--radix-popover-content-available-height,720px))]"
      >
        <CommandEmpty>{emptyMessage}</CommandEmpty>
        {includeAll ? (
          <CommandGroup className="p-0">
            <CommandItem
              value={`${allLabel} __all_accounts__`}
              onSelect={() => onChange({ kind: "all" })}
              className={cn(
                "w-full cursor-pointer gap-2 rounded-none font-inter text-sm font-medium text-neutral-800",
                size === "md" ? "h-10 px-3.5 py-2" : "h-8 px-3 py-1.5"
              )}
            >
              <i
                aria-hidden
                className="fa-regular fa-diagram-project size-5 shrink-0 text-center text-[16px] leading-5 text-blue-500"
              />
              <span className="min-w-0 flex-1 truncate">{allLabel}</span>
              {value?.kind === "all" ? (
                <>
                  <span className="sr-only">Selected</span>
                  <i
                    aria-hidden
                    className="fa-regular fa-check ml-auto size-5 text-center text-[16px] leading-5 text-blue-500"
                  />
                </>
              ) : null}
            </CommandItem>
          </CommandGroup>
        ) : null}
        {normalized.groups.map((group, index) => {
          const selectedAccountIndex = (group.accounts || []).findIndex(
            (account) => value?.kind === "account" && account.id === value.id
          );
          return (
            <CommandGroup
              key={group.id}
              heading={selectableGroups ? undefined : group.label}
              className={cn(
                "p-0",
                (index > 0 || includeAll) && "border-t border-neutral-100",
                selectableGroups && (size === "md" ? "pb-2.5" : "pb-1.5"),
                !selectableGroups && (size === "md" ? "pb-0.5" : "pb-1"),
                !selectableGroups &&
                  "[&_[cmdk-group-heading]]:px-3.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:font-inter [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:leading-4 [&_[cmdk-group-heading]]:text-neutral-500",
                !selectableGroups &&
                  size === "sm" &&
                  "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-2.5"
              )}
            >
              {selectableGroups ? (
                <CommandItem
                  value={`${group.label} ${group.id}`}
                  disabled={group.disabled}
                  title={group.disabledReason}
                  onSelect={() => onChange({ kind: "group", id: group.id })}
                  className={cn(
                    "w-full cursor-pointer gap-2 rounded-none font-inter text-sm font-medium text-neutral-800",
                    size === "md" ? "h-10 px-3.5 py-2" : "h-8 px-3 py-1.5"
                  )}
                >
                  <i
                    aria-hidden
                    className="fa-regular fa-share-nodes size-5 shrink-0 text-center text-[16px] leading-5 text-blue-500"
                  />
                  <span className="min-w-0 flex-1 truncate">{group.label}</span>
                  {value?.kind === "group" && value.id === group.id ? (
                    <>
                      <span className="sr-only">Selected</span>
                      <i
                        aria-hidden
                        className="fa-regular fa-check ml-auto size-5 text-center text-[16px] leading-5 text-blue-500"
                      />
                    </>
                  ) : null}
                </CommandItem>
              ) : null}
              {(group.accounts || []).map((account, accountIndex) => (
                <div
                  key={account.id}
                  className={cn(
                    "relative",
                    selectableGroups &&
                      "before:absolute before:top-0 before:bottom-0 before:w-px before:content-['']",
                    selectableGroups &&
                      (accountIndex <= selectedAccountIndex
                        ? "before:bg-blue-200"
                        : "before:bg-neutral-100"),
                    selectableGroups &&
                      (size === "md" ? "before:left-6" : "before:left-[22px]"),
                    selectableGroups &&
                      accountIndex === (group.accounts || []).length - 1 &&
                      "before:bottom-2"
                  )}
                >
                  <AccountMenuItem
                    account={account}
                    size={size}
                    nested={selectableGroups}
                    selected={
                      value?.kind === "account" && value.id === account.id
                    }
                    onSelect={() =>
                      onChange({ kind: "account", id: account.id })
                    }
                  />
                </div>
              ))}
            </CommandGroup>
          );
        })}
        {normalized.ungrouped.length > 0 ? (
          <CommandGroup
            heading={normalized.groups.length ? "Ungrouped" : undefined}
            className={cn(
              "border-t border-neutral-100 p-0",
              selectableGroups && (size === "md" ? "pb-2.5" : "pb-1.5"),
              !selectableGroups && (size === "md" ? "pb-0.5" : "pb-1"),
              "[&_[cmdk-group-heading]]:px-3.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:font-inter [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:leading-4 [&_[cmdk-group-heading]]:text-neutral-500",
              size === "sm" &&
                "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-2.5"
            )}
          >
            {normalized.ungrouped.map((account, accountIndex) => {
              return (
                <div
                  key={account.id}
                  className={cn(
                    "relative",
                    selectableGroups &&
                      "before:absolute before:top-0 before:bottom-0 before:w-px before:content-['']",
                    selectableGroups &&
                      (accountIndex <= selectedUngroupedIndex
                        ? "before:bg-blue-200"
                        : "before:bg-neutral-100"),
                    selectableGroups &&
                      (size === "md" ? "before:left-6" : "before:left-[22px]"),
                    selectableGroups &&
                      accountIndex === normalized.ungrouped.length - 1 &&
                      "before:bottom-2"
                  )}
                >
                  <AccountMenuItem
                    account={account}
                    size={size}
                    nested={selectableGroups}
                    selected={
                      value?.kind === "account" && value.id === account.id
                    }
                    onSelect={() =>
                      onChange({ kind: "account", id: account.id })
                    }
                  />
                </div>
              );
            })}
          </CommandGroup>
        ) : null}
      </CommandList>
    </Command>
  );
}
